<script setup>
import { computed, inject, onMounted, ref, watch } from "vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ArgonStatus from "@/components/ArgonStatus.vue";
import ArgonTable from "@/components/table/ArgonTable.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import UserTypeBadge from "@/components/badges/UserType.vue";
import { argonTableStore } from "@/_store/argonTableStore";
import { planStore } from "@/_store/planStore";
import { stopStore } from "@/_store/stopStore";
import { decaStore } from "@/_store/decaStore";
import KanbanActionsDropdown from "@/components/dropdown/ActionsDropdown.vue";
import StopDetailModal from "../stop-detail/StopDetailModal.vue";
import MoveRouteStopModal from "../MoveRouteStopModal.vue";
import DecaReviewModal from "../DecaReviewModal.vue";
import CreateUnloadStopModal from "../CreateUnloadStopModal.vue";
import CreateLoadStopModal from "../CreateLoadStopModal.vue";
import VehicleAvatar from "../../../../components/VehicleAvatar.vue";
import CopyValue from "../../../../components/CopyValue.vue";
import { errorUtils, fileUtils } from "@/utils";
import {
  isPlanEditable,
  isPlanFinalized,
  normalizePlanStatus,
} from "../../utils/planStatus";
import {
  formatAlertsTooltipHtml,
  formatDeliveryInfoSummary,
  formatStopTypeLabel,
  formatTimeWindow,
  formatVolume,
  formatWeight,
  hasDeliveryInfoSummary,
  normalizeAlerts,
} from "../../../../utils/formatters";
import ESTATUS_ENUM from "@/Constans/EstatusEnum";

const props = defineProps({
  isActive: {
    type: Boolean,
    default: false,
  },
  planId: {
    type: String,
    default: "",
  },
  planStatus: {
    type: String,
    default: "",
  },
});

const stopsStore = stopStore();
const plannerStore = planStore();
const tableStore = argonTableStore();
const decaStores = decaStore();
const emit = defineEmits(["kanban-action"]);
const sweetAlert = inject("$swal", null);

const selectedStop = ref(null);
const showStopDetailModal = ref(false);
const showMoveRouteStopModal = ref(false);
const selectedMoveRouteStopId = ref("");
const selectedMoveSourceRouteId = ref("");
const pendingBulkMoveGroups = ref([]);
const snackbar = ref(null);
const savedFilters = ref([]);
const showDecaReviewModal = ref(false);
const decaReviewStopId = ref("");
const decaReviewIsModify = ref(false);
const showCreateUnloadStopModal = ref(false);
const selectedUnloadStopRouteId = ref("");
const selectedUnloadStopId = ref("");
const showCreateLoadStopModal = ref(false);
const selectedLoadStopRouteId = ref("");
const selectedLoadStopId = ref("");
const statusOptions = Object.entries(ESTATUS_ENUM.route_stop).map(
  ([value, meta]) => ({
    label: meta?.label || value,
    value,
  })
);
const columns = Object.freeze([
  { header: "Vehículo", field: "vehicle", sort: false, position: "start" },
  { header: "Nº Parada", field: "number", sort: false, position: "start" },
  { header: "Tipo", field: "type", sort: false, position: "center" },
  { header: "Cargador", field: "shipper_info", sort: false, position: "start" },
  { header: "Horario", field: "time_window_1", sort: false, position: "start" },
  {
    header: "Dirección",
    field: "address_normalized.address",
    sort: false,
    position: "start",
  },
  { header: "Peso", field: "weight", sort: false, position: "start" },
  {
    header: "Info reparto",
    field: "delivery_info.summary",
    sort: false,
    position: "center",
  },
  {
    header: "DeCA",
    field: "has_deca",
    sort: false,
    position: "center",
    width: "w-5",
  },
  {
    header: "Estado",
    field: "status",
    sort: false,
    position: "center",
    width: "w-1",
  },
  {
    header: "",
    field: "actions",
    sort: false,
    position: "center",
    width: "w-1",
  },
]);

const fieldFilters = Object.freeze([
  { field: "number", description: "Nº parada", exact: true },
  { field: "shipper_info.name", description: "Cargador" },
  { field: "address_normalized.address", description: "Dirección" },
  { field: "assignment_info.vehicle_name", description: "Vehículo" },
]);

const defaultFilters = Object.freeze([
  {
    field: "delivery_info.status",
    label: "Estado de parada",
    multi: true,
    options: statusOptions,
  },
  {
    field: "assignment_info_has_alerts",
    label: "Alertas",
    multi: false,
    options: [
      { label: "Con alertas", value: true },
      { label: "Sin alertas", value: false },
    ],
  },
  {
    field: "has_deca",
    label: "DeCA",
    multi: false,
    options: [
      { label: "Sí", value: true },
    ],
  },
]);

const normalizedPlanStatus = computed(() => normalizePlanStatus(props.planStatus));
const isFinalizedPlan = computed(() => isPlanFinalized(normalizedPlanStatus.value));
const disableRouteStopActions = computed(
  () => isFinalizedPlan.value
);
const canUnassignRouteStops = computed(
  () => isPlanEditable(normalizedPlanStatus.value)
);
function isLoadUnloadManualType(manualType) {
  return manualType === "CARGA" || manualType === "DESCARGA";
}

function getRouteStopActions(stopItem) {
  const decaGenerated = Boolean(stopItem?.has_deca);
  // El DeCA solo aplica a paradas de tipo ENTREGA — una recogida (o una
  // parada de CARGA, que es de tipo RECOGIDA) ni siquiera muestra las
  // acciones de generar/imprimir.
  const isEntrega = getStopTypeValue(stopItem || {}) === "ENTREGA";
  const isLoadUnload = isLoadUnloadManualType(stopItem?.manual_type);

  const actions = [
    [
      {
        label: "Detalle",
        event: "detail",
      },
    ],
    [
      ...(isLoadUnload
        ? []
        : [
            {
              label: "Cambiar de ruta",
              event: "moveToVehicle",
              disabled: disableRouteStopActions.value,
            },
          ]),
    ],
  ];

  if (isLoadUnload) {
    actions[1].push({
      label: "Modificar",
      event: "modifyLoadUnload",
      disabled: disableRouteStopActions.value,
    });
  }

  if (isEntrega) {
    const canModifyDeca = isPlanEditable(normalizedPlanStatus.value);
    const status = String(stopItem?.status || "").toUpperCase();
    const canManageDeca = !["FINALIZADO", "FINALIZADO_PARCIAL", "INCIDENCIA"].includes(status);
    actions.push([
      decaGenerated
        ? {
            label: "Modificar DeCA",
            event: "generateDeca",
            disabled: !canModifyDeca || !canManageDeca,
            tooltip: !canModifyDeca
              ? "No se puede modificar el DeCA de una planificación finalizada."
              : !canManageDeca
                ? "La parada ya está finalizada, no se puede generar/modificar su DeCA."
                : "",
          }
        : {
            label: "Generar DeCA",
            event: "generateDeca",
            disabled: !canManageDeca,
            tooltip: canManageDeca
              ? ""
              : "La parada ya está finalizada, no se puede generar/modificar su DeCA.",
          },
      {
        label: "Imprimir DeCA",
        event: "printDeca",
        disabled: !decaGenerated,
        tooltip: decaGenerated ? "" : "Esta parada no tiene DeCA generado.",
      },
    ]);
  }

  actions.push([
    {
      label: "Desasignar",
      event: "unassign",
      disabled: disableRouteStopActions.value,
    },
  ]);

  return actions;
}

const routeStopRows = computed(() => {
  const source = getStopsSource(stopsStore.stops);
  const data = source.map((item) => ({
    ...item,
    _id: item._id || item.stop_id || item.assignment_info?.route_stop_id,
  }));

  const totalFromResponse = Number.parseInt(stopsStore.stops?.total, 10);
  const total = Number.isFinite(totalFromResponse)
    ? totalFromResponse
    : data.length;

  return {
    total,
    data,
  };
});

const tableItems = computed(() => routeStopRows.value);

onMounted(() => {
  tableStore.clear();
  tableStore.pagination.limit = 1000;
  tableStore.changeFilters(savedFilters.value);
});

watch(
  () => [props.isActive, props.planId],
  ([isActive]) => {
    if (!isActive) return;
    tableStore.clearSelection();
    tableStore.changeFilters(savedFilters.value);
    fetchRouteStops();
  },
  { immediate: true }
);

watch(
  () => tableStore.filters,
  (value) => {
    if (!props.isActive) return;
    savedFilters.value = Array.isArray(value)
      ? JSON.parse(JSON.stringify(value))
      : [];
  },
  { deep: true }
);

watch(
  () => stopsStore.error,
  (value) => {
    if (!value) return;
    snackbar.value = {
      title: "Error",
      message: value?.message || "No se pudieron cargar las paradas de rutas.",
      type: "danger",
      color: "white",
    };
  }
);

watch(
  () => plannerStore.kanbanRoutesUpdated,
  (value) => {
    if (!value || !props.isActive) return;
    fetchRouteStops();
  }
);

function getPosition(field) {
  const column = columns.find((col) => col.field === field);
  return column?.position || "start";
}

function toSafeString(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function getStopsSource(stopsValue) {
  if (Array.isArray(stopsValue?.data)) return stopsValue.data;
  if (Array.isArray(stopsValue)) return stopsValue;
  return [];
}

function getWarningItems(item) {
  return normalizeAlerts(item?.assignment_info?.alerts);
}

function getAssignmentInfo(item) {
  return item?.assignment_info || {};
}

function getStopAddress(item) {
  return (
    item?.address_normalized?.address ||
    item?.address_normalized?.address ||
    ""
  );
}

function closeSnackbar() {
  snackbar.value = null;
}

function getSelectedStopIds() {
  if (!Array.isArray(tableStore.checkedList)) return [];
  return [
    ...new Set(
      tableStore.checkedList.map((id) => toSafeString(id)).filter(Boolean)
    ),
  ];
}

function resolveSelectedStops() {
  const selectedIds = new Set(getSelectedStopIds());
  const rows = Array.isArray(tableItems.value?.data)
    ? tableItems.value.data
    : [];
  return rows.filter((item) => selectedIds.has(toSafeString(item?._id)));
}

function fetchRouteStops() {
  closeSnackbar();
  const pagination = tableStore.pagination;
  const query = {
    ...(tableStore.query || {}),
    "assignment_info.plan_id": props.planId,
  };
  const sort = tableStore.sort;

  stopsStore.getStops(pagination, query, sort);
}

async function exportRouteStops() {
  const bytes = await stopsStore.exportar(
    tableStore.pagination,
    {
      ...(tableStore.query || {}),
      "assignment_info.plan_id": props.planId,
    },
    tableStore.sort
  );

  fileUtils.downloadBytesFile(
    bytes,
    fileUtils.generateDownloadFileName("xlsx")
  );
}

function reloadPage() {
  if (!props.isActive) return;
  fetchRouteStops();
}

function onSortChanged() {
  tableStore.clearPagination();
  reloadPage();
}

async function unassignStop() {
  closeSnackbar();
  if (!canUnassignRouteStops.value) return;
  const selectedStops = resolveSelectedStops();
  if (selectedStops.length === 0) {
    snackbar.value = {
      title: "Error",
      message: "Debe seleccionar al menos una parada para desasignar.",
      type: "danger",
      color: "white",
    };
    return;
  }

  const routeStopIds = [
    ...new Set(
      selectedStops
        .map((item) => toSafeString(item.assignment_info.route_stop_id))
        .filter(Boolean)
    ),
  ];

  if (routeStopIds.length === 0) {
    snackbar.value = {
      title: "Error",
      message: "No se encontraron route_stop_id válidos en la selección.",
      type: "danger",
      color: "white",
    };
    return;
  }

  const confirmOptions = {
    title: "¿Está seguro de querer desasignar las paradas seleccionadas?",
    text: "Las paradas volverán al panel de no asignadas.",
    showCancelButton: true,
    confirmButtonText: "Desasignar",
    cancelButtonText: "Cancelar",
    reverseButtons: true,
    customClass: {
      confirmButton: "btn btn-xs ms-1 bg-gradient-danger",
      cancelButton: "btn btn-xs btn-outline-primary me-1",
    },
    buttonsStyling: false,
  };

  const result =
    typeof sweetAlert === "function"
      ? await sweetAlert(confirmOptions)
      : {
          isConfirmed: window.confirm(
            "¿Está seguro de querer desasignar las paradas seleccionadas?"
          ),
        };
  if (!result?.isConfirmed) return;

  emit("kanban-action", {
    type: "unassign-route-stop",
    payload: {
      routeId: "route_id",
      routeStopId: routeStopIds.join(","),
    },
  });
}

async function moveRouteStopsToRoute() {
  closeSnackbar();
  if (!canUnassignRouteStops.value) return;
  const selectedStops = resolveSelectedStops();
  if (selectedStops.length === 0) {
    snackbar.value = {
      title: "Error",
      message: "Debe seleccionar al menos una parada para cambiar de ruta.",
      type: "danger",
      color: "white",
    };
    return;
  }

  const groupMap = new Map();
  for (const stop of selectedStops) {
    const sourceRouteId = toSafeString(stop.assignment_info?.route_id);
    const routeStopId = toSafeString(stop.assignment_info?.route_stop_id);
    if (!sourceRouteId || !routeStopId) continue;
    if (!groupMap.has(sourceRouteId)) groupMap.set(sourceRouteId, []);
    groupMap.get(sourceRouteId).push(routeStopId);
  }

  const groups = Array.from(groupMap.entries()).map(([sourceRouteId, routeStopIds]) => ({
    sourceRouteId,
    routeStopIds,
  }));

  if (groups.length === 0) {
    snackbar.value = {
      title: "Error",
      message: "No se encontraron route_stop_id válidos en la selección.",
      type: "danger",
      color: "white",
    };
    return;
  }

  if (groups.length > 1) {
    snackbar.value = {
      title: "Error",
      message: "Solo puedes cambiar de ruta paradas que pertenezcan a la misma ruta origen.",
      type: "danger",
      color: "white",
    };
    return;
  }

  pendingBulkMoveGroups.value = groups;
  selectedMoveRouteStopId.value = "bulk";
  selectedMoveSourceRouteId.value = "";
  showMoveRouteStopModal.value = true;
}

function getContactName(contact) {
  if (typeof contact === "string") return contact;
  return toSafeString(contact?.name);
}

function getStopTypeValue(stopItem) {
  return toSafeString(stopItem.type || stopItem.stop_type).toUpperCase();
}

function getStatusTooltip(item) {
  const status = toSafeString(item?.delivery_info?.status).toUpperCase();
  const description = toSafeString(item?.delivery_info?.status_description);

  if (status === "INCIDENCIA" && description) {
    return description;
  }

  return "";
}

function getActionsMenuId(item) {
  const assignmentInfo = getAssignmentInfo(item);
  const routeId = toSafeString(assignmentInfo.route_id);
  const routeStopId = toSafeString(assignmentInfo.route_stop_id);
  return `route-stop-actions-${routeId.replace(
    /[^a-zA-Z0-9_-]/g,
    "_"
  )}-${routeStopId.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
}

function closeStopDetailModal() {
  showStopDetailModal.value = false;
  selectedStop.value = null;
}

function closeMoveRouteStopModal() {
  showMoveRouteStopModal.value = false;
  selectedMoveRouteStopId.value = "";
  selectedMoveSourceRouteId.value = "";
  pendingBulkMoveGroups.value = [];
}

function closeCreateUnloadStopModal() {
  showCreateUnloadStopModal.value = false;
  selectedUnloadStopRouteId.value = "";
  selectedUnloadStopId.value = "";
}

function openModifyUnloadStopModal(stopItem) {
  const routeId = toSafeString(getAssignmentInfo(stopItem).route_id);
  const stopId = toSafeString(stopItem?._id);
  if (!routeId || !stopId) {
    snackbar.value = {
      title: "Error",
      message: "No se encontro la parada de descarga a modificar.",
      type: "danger",
      color: "white",
    };
    return;
  }

  snackbar.value = null;
  selectedUnloadStopRouteId.value = routeId;
  selectedUnloadStopId.value = stopId;
  showCreateUnloadStopModal.value = true;
}

function onCreateUnloadStopConfirm(payload) {
  const routeId = selectedUnloadStopRouteId.value;
  if (!routeId) {
    closeCreateUnloadStopModal();
    return;
  }

  emit("kanban-action", {
    type: "create-unload-stop",
    payload: {
      routeId,
      warehouseId: payload?.warehouseId || "",
      taskIds: payload?.taskIds || [],
      newOrder: 1,
      stopId: payload?.stopId || null,
    },
  });

  closeCreateUnloadStopModal();
}

function closeCreateLoadStopModal() {
  showCreateLoadStopModal.value = false;
  selectedLoadStopRouteId.value = "";
  selectedLoadStopId.value = "";
}

function openModifyLoadStopModal(stopItem) {
  const routeId = toSafeString(getAssignmentInfo(stopItem).route_id);
  const stopId = toSafeString(stopItem?._id);
  if (!routeId || !stopId) {
    snackbar.value = {
      title: "Error",
      message: "No se encontro la parada de carga a modificar.",
      type: "danger",
      color: "white",
    };
    return;
  }

  snackbar.value = null;
  selectedLoadStopRouteId.value = routeId;
  selectedLoadStopId.value = stopId;
  showCreateLoadStopModal.value = true;
}

function onCreateLoadStopConfirm(payload) {
  const routeId = selectedLoadStopRouteId.value;
  if (!routeId) {
    closeCreateLoadStopModal();
    return;
  }

  emit("kanban-action", {
    type: "create-load-stop",
    payload: {
      routeId,
      warehouseId: payload?.warehouseId || "",
      taskIds: payload?.taskIds || [],
      newOrder: 1,
      stopId: payload?.stopId || null,
    },
  });

  closeCreateLoadStopModal();
}

function openMoveRouteStopModal(stopItem) {
  selectedMoveRouteStopId.value = stopItem?.assignment_info?.route_stop_id;
  selectedMoveSourceRouteId.value = stopItem?.assignment_info?.route_id;
  showMoveRouteStopModal.value = true;
}

function openDecaReviewForStop(stopItem) {
  const stopId = toSafeString(stopItem?._id);
  if (!stopId) return;
  decaReviewStopId.value = stopId;
  decaReviewIsModify.value = Boolean(stopItem?.has_deca);
  showDecaReviewModal.value = true;
}

function generateDecaForStop(stopItem) {
  openDecaReviewForStop(stopItem);
}

function closeDecaReviewModal() {
  showDecaReviewModal.value = false;
  decaReviewStopId.value = "";
}

function onDecaReviewConfirm() {
  const message = decaReviewIsModify.value
    ? "DeCA modificado correctamente"
    : "DeCA generado correctamente";
  closeDecaReviewModal();
  reloadPage();
  snackbar.value = {
    title: "OK",
    message,
    type: "info",
    color: "white",
  };
}

function printDeca(stopIds) {
  decaStores
    .printDeca(stopIds)
    .then((bytes) => {
      fileUtils.openBytesFile(bytes, "application/pdf");
    })
    .catch((error) => {
      snackbar.value = {
        title: "Error",
        message: errorUtils.getErrorMessageHtml(
          error,
          "No se pudo imprimir el DeCA."
        ),
        type: "danger",
        color: "white",
      };
    });
}

function printDecaForStop(stopItem) {
  const stopId = stopItem?._id;
  if (!stopId) return;
  printDeca([stopId]);
}

async function onRouteStopAction(stopItem, action) {
  const actionEvent = action?.event;
  if (!stopItem || !actionEvent) return;

  if (actionEvent === "detail") {
    const stopId = toSafeString(stopItem._id);
    if (!stopId) {
      snackbar.value = {
        title: "Error",
        message: "No se encontro stop_id para consultar el detalle.",
        type: "danger",
        color: "white",
      };
      return;
    }

    selectedStop.value = stopItem;
    showStopDetailModal.value = true;
    return;
  }

  if (actionEvent === "moveToVehicle") {
    openMoveRouteStopModal(stopItem);
    return;
  }

  if (actionEvent === "modifyLoadUnload") {
    if (stopItem?.manual_type === "CARGA") {
      openModifyLoadStopModal(stopItem);
    } else {
      openModifyUnloadStopModal(stopItem);
    }
    return;
  }

  if (actionEvent === "generateDeca") {
    generateDecaForStop(stopItem);
    return;
  }

  if (actionEvent === "printDeca") {
    printDecaForStop(stopItem);
    return;
  }

  if (actionEvent !== "unassign") return;

  const assignmentInfo = getAssignmentInfo(stopItem);
  const routeId = assignmentInfo.route_id;
  const routeStopId = assignmentInfo.route_stop_id;

  const confirmOptions = {
    title: "¿Está seguro de querer desasignar esta parada?",
    text: "La parada volverá al panel de no asignadas.",
    showCancelButton: true,
    confirmButtonText: "Desasignar",
    cancelButtonText: "Cancelar",
    reverseButtons: true,
    customClass: {
      confirmButton: "btn btn-xs ms-1 bg-gradient-danger",
      cancelButton: "btn btn-xs btn-outline-primary me-1",
    },
    buttonsStyling: false,
  };

  const result =
    typeof sweetAlert === "function"
      ? await sweetAlert(confirmOptions)
      : {
          isConfirmed: window.confirm(
            "¿Está seguro de querer desasignar esta parada?"
          ),
        };
  if (!result?.isConfirmed) return;

  emit("kanban-action", {
    type: "unassign-route-stop",
    payload: {
      routeId,
      routeStopId,
    },
  });
}

function onMoveRouteStopConfirm(payload) {
  const targetRouteId = payload.targetRouteId;

  if (selectedMoveRouteStopId.value === "bulk") {
    const groups = pendingBulkMoveGroups.value.filter(
      (g) => g.sourceRouteId !== targetRouteId
    );
    if (groups.length > 0) {
      emit("kanban-action", {
        type: "bulk-move-route-stops",
        payload: { groups, targetRouteId },
      });
    }
  } else {
    emit("kanban-action", {
      type: "move-route-stop",
      payload: {
        sourceRouteId: selectedMoveSourceRouteId.value,
        routeStopIds: [selectedMoveRouteStopId.value],
        targetRouteId,
        newOrder: null,
      },
    });
  }

  closeMoveRouteStopModal();
}
</script>

<template>
  <section class="card h-100 d-flex flex-column">
    <argon-table
      :columns="columns"
      :items="tableItems"
      :field-filters="fieldFilters"
      :default-filters="defaultFilters"
      class="p-2 pb-0 flex-grow-1"
      :show-new-button="false"
      :show-check-input="true"
      :show-importar="false"
      :show-filters="true"
      :table-hover="true"
      @filter-changed="reloadPage"
      @on-reload="reloadPage"
      @sort-changed="onSortChanged"
      @page-changed="reloadPage"
      @export-data="exportRouteStops"
      @on-click="(item) => onRouteStopAction(item, { event: 'detail' })"
    >
      <template #buttons>
        <div
          class="dropdown d-inline"
          data-dropdown-static
          style="--dropdown-gap: 4px"
        >
          <argon-button
            id="navbarDropdownPrinter"
            color="primary"
            variant="outline"
            class="dropdown-toggle me-2 btn-xs"
            data-bs-toggle="dropdown"
            data-bs-display="static"
            aria-expanded="false"
            ><i class="fa fa-exchange me-sm-2"></i>Acciones</argon-button
          >
          <ul
            class="dropdown-menu z-index-1030 dropdown-menu-lg-start px-2 py-3"
            aria-labelledby="navbarDropdownPrinter"
            style
          >
            <li>
              <a
                class="dropdown-item border-radius-md"
                :class="{ disabled: !canUnassignRouteStops }"
                @click="unassignStop"
                >Desasignar</a
              >
            </li>
            <li>
              <a
                class="dropdown-item border-radius-md"
                :class="{ disabled: !canUnassignRouteStops }"
                @click="moveRouteStopsToRoute"
                >Cambiar de ruta</a
              >
            </li>
          </ul>
        </div>
      </template>
      <template #default="{ item }">
        <argon-td
          :position="getPosition('vehicle')"
          :text-primary="getAssignmentInfo(item).vehicle_name"
          :text-secondary="getAssignmentInfo(item).vehicle_model"
        >
          <template #prepend>
            <vehicle-avatar
              :label="getAssignmentInfo(item).vehicle_name"
              :abbreviation="getAssignmentInfo(item).vehicle_abbreviation"
              :accent-color="getAssignmentInfo(item).route_color_hex"
              :size="28"
              :has-alert="getWarningItems(item).length > 0"
              :alert-tooltip="formatAlertsTooltipHtml(getWarningItems(item))"
              alert-label="Alertas de parada"
            />
          </template>
        </argon-td>
        <argon-td
          :text-primary="item.number"
          :position="getPosition('number')"
        >
          <template v-if="item.number" #append>
            <copy-value :value-to-copy="String(item.number)" size="xs" show-on-hover />
          </template>
        </argon-td>
        <argon-td :position="getPosition('type')">
          <user-type-badge
            style="min-width: 100px"
            class="badge badge-md"
            :item="formatStopTypeLabel(item)"
          />
        </argon-td>
        <argon-td
          :text-primary="item.shipper_info?.name || ''"
          :text-secondary="getContactName(item.contact)"
          :position="getPosition('shipper_info')"
        />   
        <argon-td
          :text-primary="formatTimeWindow(item.time_window_1)"
          :text-secondary="formatTimeWindow(item.time_window_2)"
          :position="getPosition('time_window_1')"
        />
        <argon-td
          :text-primary="getStopAddress(item)"
          :position="getPosition('address_normalized.address')"
          max-width="150px"
        />
        <argon-td
          :text-primary="formatWeight(getAssignmentInfo(item).weight)"
          :text-secondary="
            formatVolume(getAssignmentInfo(item).volumetric_weight, 3)
          "
          :position="getPosition('weight')"
        />
        <argon-td
          :position="getPosition('delivery_info.summary')"
        >
          <i
            v-if="hasDeliveryInfoSummary(item)"
            v-tooltip="formatDeliveryInfoSummary(item)"
            class="fa-solid fa-circle-info grid-icon-primary grid-icon-info"
            aria-label="Info reparto"
          />
        </argon-td>
        <argon-td :position="getPosition('has_deca')">
          <i
            v-if="item.has_deca === true"
            v-tooltip="'DeCA generado'"
            class="fa fa-check grid-icon-primary text-xs"
            aria-label="DeCA generado"
          ></i>
        </argon-td>
        <argon-td :position="getPosition('status')">
          <argon-status
            entity="route_stop"
            :status="item?.delivery_info?.status"
            :tooltip-external-text="getStatusTooltip(item)"
            width="100px"
          />
        </argon-td>
        <argon-td :position="getPosition('actions')">
          <div class="task-action-slot">
            <kanban-actions-dropdown
              class="task-menu-wrap"
              :menu-id="getActionsMenuId(item)"
              :actions="getRouteStopActions(item)"
              aria-label="Acciones de parada"
              trigger-class="task-actions-button"
              menu-class="task-menu-dropdown"
              @action="(action) => onRouteStopAction(item, action)"
            />
          </div>
        </argon-td>
      </template>
    </argon-table>
  </section>

  <stop-detail-modal
    v-if="showStopDetailModal && selectedStop"
    modal-id="route-stop-table-detail-modal"
    :stop-id="selectedStop._id"
    @close="closeStopDetailModal"
  />

  <move-route-stop-modal
    v-if="showMoveRouteStopModal && selectedMoveRouteStopId"
    modal-id="route-stop-table-move-route-modal"
    :plan-id="planId"
    :route-stop-id="selectedMoveRouteStopId"
    :source-route-id="selectedMoveSourceRouteId"
    @confirm="onMoveRouteStopConfirm"
    @close="closeMoveRouteStopModal"
  />

  <deca-review-modal
    v-if="showDecaReviewModal && decaReviewStopId"
    modal-id="route-stop-table-deca-review-modal"
    :stop-id="decaReviewStopId"
    @close="closeDecaReviewModal"
    @confirm="onDecaReviewConfirm"
  />

  <create-unload-stop-modal
    v-if="showCreateUnloadStopModal && selectedUnloadStopRouteId"
    modal-id="route-stop-table-create-unload-stop-modal"
    :route-id="selectedUnloadStopRouteId"
    :stop-id="selectedUnloadStopId"
    @confirm="onCreateUnloadStopConfirm"
    @close="closeCreateUnloadStopModal"
  />

  <create-load-stop-modal
    v-if="showCreateLoadStopModal && selectedLoadStopRouteId"
    modal-id="route-stop-table-create-load-stop-modal"
    :route-id="selectedLoadStopRouteId"
    :stop-id="selectedLoadStopId"
    @confirm="onCreateLoadStopConfirm"
    @close="closeCreateLoadStopModal"
  />

  <div class="position-fixed top-0 end-0 z-index-2 p-3">
    <argon-snackbar
      v-if="snackbar"
      :title="snackbar.title"
      :description="snackbar.message"
      :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
      :color="snackbar.color"
      :close-handler="closeSnackbar"
    />
  </div>
</template>

<style scoped lang="scss">
@use "../planner-surface-shared";

.task-action-slot {
  @extend .planner-action-slot;
}
.task-menu-wrap {
  @extend .planner-action-wrap;
}
.task-menu-dropdown {
  @extend .planner-action-dropdown;
}
:deep(thead th.text-center.w-1:last-child),
:deep(tbody td.text-center:last-child) {
  min-width: 48px;
  width: 48px;
}
</style>
